import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, Card, CardHeader, CardContent, Divider, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Button, Chip, LinearProgress } from '@mui/material';
import { ArrowDownward, ArrowUpward, Star, CalendarToday, Launch } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { StatCardSkeleton } from './LoadingSkeleton';
import '../styles/ContractDetails.css';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return 'Invalid date';
  }
};

// Helper function to convert points to hours
const convertToHours = (points, dollarPerHour) => {
  if (!points || !dollarPerHour || dollarPerHour === 0) return 0;
  return ((parseFloat(points) / parseFloat(dollarPerHour)) * 100).toFixed(2);
};

// Helper function to get display value and label based on contract display type
const getDisplayValueAndLabel = (points, contract) => {
  if (contract?.customer_display_type === 'hours' && contract?.dollar_per_hour) {
    return {
      value: convertToHours(points, contract.dollar_per_hour),
      label: 'Hours'
    };
  }
  return {
    value: points,
    label: 'Points'
  };
};

const ClientDashboard = () => {
  const { contractId } = useParams();
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  // State for each section
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [goalsError, setGoalsError] = useState(null);

  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [performanceError, setPerformanceError] = useState(null);

  const [summaryTasks, setSummaryTasks] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [waitingTasks, setWaitingTasks] = useState([]);
  const [waitingLoading, setWaitingLoading] = useState(true);
  const [waitingError, setWaitingError] = useState(null);

  const [comingUpTasks, setComingUpTasks] = useState([]);
  const [comingUpLoading, setComingUpLoading] = useState(true);
  const [comingUpError, setComingUpError] = useState(null);

  const [completedTasks, setCompletedTasks] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(true);
  const [completedError, setCompletedError] = useState(null);

  // Contract data state
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [contractError, setContractError] = useState(null);

  // Bulletproof anchor refs
  const waitingRef = useRef(null);
  const comingUpRef = useRef(null);
  const completedRef = useRef(null);

  // Fetch contract goals
  useEffect(() => {
    setGoalsLoading(true);
    setGoalsError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/goals/${contractId}`)
      .then(res => res.json())
      .then(data => setGoals(data))
      .catch(err => setGoalsError('Failed to load goals'))
      .finally(() => setGoalsLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch contract performance
  useEffect(() => {
    setPerformanceLoading(true);
    setPerformanceError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/performance/${contractId}`)
      .then(res => res.json())
      .then(data => {
        console.log('Performance API response:', data);
        setPerformance(data);
      })
      .catch(err => setPerformanceError('Failed to load performance'))
      .finally(() => setPerformanceLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch summary tasks
  useEffect(() => {
    setSummaryLoading(true);
    setSummaryError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/tasks/summary/${contractId}`)
      .then(res => res.json())
      .then(data => setSummaryTasks(data))
      .catch(err => setSummaryError('Failed to load summary tasks'))
      .finally(() => setSummaryLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch waiting on you tasks
  useEffect(() => {
    setWaitingLoading(true);
    setWaitingError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/tasks/waiting/${contractId}`)
      .then(res => res.json())
      .then(data => setWaitingTasks(Array.isArray(data) ? data.filter(t => !t.is_deleted && !t.is_archived) : []))
      .catch(err => setWaitingError('Failed to load waiting tasks'))
      .finally(() => setWaitingLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch coming up tasks
  useEffect(() => {
    setComingUpLoading(true);
    setComingUpError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/tasks/coming-up/${contractId}`)
      .then(res => res.json())
      .then(data => setComingUpTasks(Array.isArray(data) ? data.filter(t => !t.is_deleted && !t.is_archived) : []))
      .catch(err => setComingUpError('Failed to load coming up tasks'))
      .finally(() => setComingUpLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch recently completed tasks
  useEffect(() => {
    setCompletedLoading(true);
    setCompletedError(null);
    fetch(`${apiBaseUrl}/api/client-dashboard/tasks/completed/${contractId}`)
      .then(res => res.json())
      .then(data => setCompletedTasks(Array.isArray(data) ? data.filter(t => !t.is_deleted && !t.is_archived) : []))
      .catch(err => setCompletedError('Failed to load completed tasks'))
      .finally(() => setCompletedLoading(false));
  }, [contractId, apiBaseUrl]);

  // Fetch contract details for display type and dollar per hour
  useEffect(() => {
    setContractLoading(true);
    setContractError(null);
    fetch(`${apiBaseUrl}/api/contracts/${contractId}`)
      .then(res => res.json())
      .then(data => {
        console.log('Contract data for display conversion:', data);
        setContract(data);
      })
      .catch(err => {
        console.error('Failed to load contract details:', err);
        setContractError('Failed to load contract details');
      })
      .finally(() => setContractLoading(false));
  }, [contractId, apiBaseUrl]);

  // Helper: get contract name/number from performance
  const contractNumber = performance?.contract_number || '';
  const contractName = performance?.contract_name || '';

  // Stat card values now use the same arrays as the sections below for consistency
  const waitingCount = waitingTasks.length;
  const completedCount = completedTasks.length;
  const workingCount = comingUpTasks.length;

  // Points/financial summary
  const pointsCards = performance ? [
    { 
      label: contract?.customer_display_type === 'hours' ? 'Hours Purchased' : 'Points Purchased', 
      value: contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
        ? convertToHours(performance.points_purchased, contract.dollar_per_hour)
        : performance.points_purchased 
    },
    { 
      label: contract?.customer_display_type === 'hours' ? 'Hours Credited' : 'Points Credited', 
      value: contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
        ? convertToHours(performance.points_credited, contract.dollar_per_hour)
        : performance.points_credited 
    },
    { 
      label: contract?.customer_display_type === 'hours' ? 'Hours Delivered' : 'Points Delivered', 
      value: contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
        ? convertToHours(performance.points_delivered, contract.dollar_per_hour)
        : performance.points_delivered 
    },
    { 
      label: contract?.customer_display_type === 'hours' ? 'Hours in Working' : 'Points in Working', 
      value: contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
        ? convertToHours(performance.points_working, contract.dollar_per_hour)
        : performance.points_working 
    },
    { 
      label: contract?.customer_display_type === 'hours' ? 'Hours Balance' : 'Points Balance', 
      value: contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
        ? convertToHours(performance.points_balance_including_working, contract.dollar_per_hour)
        : performance.points_balance_including_working 
    },
  ] : [];

  if (performanceLoading) return <LinearProgress />;

  return (
    <>
      {/* Header Row: Avatar, Contract Info, Summary Cards */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4, flexWrap: 'nowrap' }}>
        {/* Left Side: Contract Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: '0 0 auto' }}>
          <Avatar sx={{ bgcolor: '#8A42EC', width: 56, height: 56, fontSize: 28, mr: 2 }}>
            {contractName[0] || '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">{contractNumber}</Typography>
            <Typography variant="h5" fontWeight={700}>{contractName}</Typography>
          </Box>
        </Box>
        {/* Right Side: Summary Cards */}
        <Box sx={{ display: 'flex', gap: 2, flex: '1 1 auto', justifyContent: 'flex-end' }}>
          {summaryLoading ? (
            <>
              <Box sx={{ width: '200px', height: '110px' }}>
                <StatCardSkeleton />
              </Box>
              <Box sx={{ width: '200px', height: '110px' }}>
                <StatCardSkeleton />
              </Box>
              <Box sx={{ width: '200px', height: '110px' }}>
                <StatCardSkeleton />
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ cursor: 'pointer', width: '200px', height: '110px', '&:hover': { transform: 'translateY(-2px)' } }}>
                <div 
                  className="stat-card" 
                  style={{ height: '100%', width: '100%' }}
                  onClick={() => { window.location.hash = '#waiting-on-you-section'; }}
                >
                  <div className="stat-value">{waitingCount}</div>
                  <div className="stat-label">Tasks waiting on client feedback</div>
                </div>
              </Box>
              <Box sx={{ cursor: 'pointer', width: '200px', height: '110px', '&:hover': { transform: 'translateY(-2px)' } }}>
                <div 
                  className="stat-card" 
                  style={{ height: '100%', width: '100%' }}
                  onClick={() => { window.location.hash = '#completed-section'; }}
                >
                  <div className="stat-value">{completedCount}</div>
                  <div className="stat-label">Tasks completed in last 30 days</div>
                </div>
              </Box>
              <Box sx={{ cursor: 'pointer', width: '200px', height: '110px', '&:hover': { transform: 'translateY(-2px)' } }}>
                <div 
                  className="stat-card" 
                  style={{ height: '100%', width: '100%' }}
                  onClick={() => { window.location.hash = '#coming-up-section'; }}
                >
                  <div className="stat-value">{workingCount}</div>
                  <div className="stat-label">Current tasks in working status</div>
                </div>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Goals Section - Only show for strategic contracts */}
        {performance && performance.engagement_type === 'strategic' && (
          <Grid item xs={12}>
            <Card className="details-card">
              <CardHeader title="Goals" subheader="Current active goals for this contract" />
              <Divider />
              <CardContent>
                {goalsLoading ? (
                  <Typography>Loading goals...</Typography>
                ) : goals && goals.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Goal</strong></TableCell>
                          <TableCell><strong>Due Date</strong></TableCell>
                          <TableCell><strong>Progress</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {goals.map((goal, index) => (
                          <TableRow key={index}>
                            <TableCell>{goal.goal_name}</TableCell>
                            <TableCell>{formatDate(goal.due_date)}</TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ 
                                  flex: '1', 
                                  height: '8px', 
                                  backgroundColor: '#e0e0e0', 
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div 
                                    style={{
                                      height: '100%',
                                      width: `${goal.percent_complete || 0}%`,
                                      backgroundColor: '#8A42EC',
                                      borderRadius: '4px',
                                      transition: 'width 0.3s ease'
                                    }}
                                  />
                                </div>
                                <span style={{ 
                                  minWidth: '40px', 
                                  fontSize: '12px', 
                                  color: '#666' 
                                }}>
                                  {goal.percent_complete || 0}%
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    no active goals for this contract
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Waiting on You Section */}
        <Grid item xs={12}>
          <div className="section-anchor" id="waiting-on-you-section" />
          <Card className="details-card">
            <CardHeader title="Waiting on You" />
            <Divider />
            <CardContent>
              {waitingLoading ? (
                <Typography>Loading tasks...</Typography>
              ) : waitingError ? (
                <Typography color="error">{waitingError}</Typography>
              ) : waitingTasks && waitingTasks.length > 0 ? (
                <Box>
                  {waitingTasks.map((task) => (
                    <Paper key={task.id} sx={{ mb: 2, p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{task.name}</Typography>
                      {task.external_description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {task.external_description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 1.5 }}>
                        {task.list_type !== 'todos' && (
                          <Chip 
                            icon={<Star sx={{ color: '#FFD700' }} />} 
                            label={`${contract?.customer_display_type === 'hours' && contract?.dollar_per_hour ? convertToHours(task.points ?? 0, contract.dollar_per_hour) : task.points ?? 0} ${contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}`} 
                            size="small" 
                            sx={{ px: 1.5, py: 2 }} 
                          />
                        )}
                        {task.due_date && <Chip icon={<CalendarToday sx={{ color: '#8A42EC' }} />} label={`Due: ${formatDate(task.due_date)}`} size="small" sx={{ px: 1.5, py: 2 }} />}
                        {task.link_to_review_tool && <Button href={task.link_to_review_tool} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Review</Button>}
                        {task.link_to_final_deliverable && <Button href={task.link_to_final_deliverable} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Final deliverable</Button>}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No tasks waiting on your feedback at this time.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* What's Coming Up Section */}
        <Grid item xs={12}>
          <a id="coming-up-section" className="section-anchor"></a>
          <Card className="details-card">
            <CardHeader title="What's Coming Up" />
            <Divider />
            <CardContent>
              {comingUpLoading ? <LinearProgress /> : comingUpError ? <Typography color="error">{comingUpError}</Typography> : (
                comingUpTasks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                    <Typography variant="body1">No upcoming tasks in the next thirty days.</Typography>
                  </Box>
                ) : (
                  <Box>
                    {comingUpTasks.map((task) => (
                      <Paper key={task.id} sx={{ mb: 2, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{task.name}</Typography>
                        {task.external_description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {task.external_description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 1.5 }}>
                          {task.list_type !== 'todos' && (
                            <Chip 
                              icon={<Star sx={{ color: '#FFD700' }} />} 
                              label={`${contract?.customer_display_type === 'hours' && contract?.dollar_per_hour ? convertToHours(task.points ?? 0, contract.dollar_per_hour) : task.points ?? 0} ${contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}`} 
                              size="small" 
                              sx={{ px: 1.5, py: 2 }} 
                            />
                          )}
                          {task.due_date && <Chip icon={<CalendarToday sx={{ color: '#8A42EC' }} />} label={`Due: ${new Date(task.due_date).toLocaleDateString()}`} size="small" sx={{ px: 1.5, py: 2 }} />}
                          {task.link_to_review_tool && <Button href={task.link_to_review_tool} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Review</Button>}
                          {task.link_to_final_deliverable && <Button href={task.link_to_final_deliverable} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Final deliverable</Button>}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recently Completed Section */}
        <Grid item xs={12}>
          <a id="completed-section" className="section-anchor"></a>
          <Card className="details-card">
            <CardHeader title="Deliverables Complete (Last 30 Days)" />
            <Divider />
            <CardContent>
              {completedLoading ? <LinearProgress /> : completedError ? <Typography color="error">{completedError}</Typography> : (
                completedTasks.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                    <Typography variant="body1">No deliverables completed in the last 30 days.</Typography>
                  </Box>
                ) : (
                  <Box>
                    {completedTasks.map((task) => (
                      <Paper key={task.id} sx={{ mb: 2, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{task.name}</Typography>
                        {task.external_description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {task.external_description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 1.5 }}>
                          {task.list_type !== 'todos' && (
                            <Chip 
                              icon={<Star sx={{ color: '#FFD700' }} />} 
                              label={`${contract?.customer_display_type === 'hours' && contract?.dollar_per_hour ? convertToHours(task.points ?? 0, contract.dollar_per_hour) : task.points ?? 0} ${contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}`} 
                              size="small" 
                              sx={{ px: 1.5, py: 2 }} 
                            />
                          )}
                          {task.date_done && <Chip icon={<CalendarToday sx={{ color: '#8A42EC' }} />} label={`Completed: ${new Date(task.date_done).toLocaleDateString()}`} size="small" sx={{ px: 1.5, py: 2 }} />}
                          {task.link_to_review_tool && <Button href={task.link_to_review_tool} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Review</Button>}
                          {task.link_to_final_deliverable && <Button href={task.link_to_final_deliverable} target="_blank" size="small" startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}>Final deliverable</Button>}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Summary */}
        <Grid item xs={12}>
          <Card className="details-card">
            <CardHeader title={`Financial Summary ${contract?.customer_display_type === 'hours' ? '(Hours)' : '(Points)'}`} />
            <Divider />
            <CardContent>
              {performanceLoading ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mt: 2 }}>
                  {Array.from({ length: 5 }, (_, index) => (
                    <StatCardSkeleton key={index} />
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mt: 2 }}>
                  {pointsCards.map((pt) => (
                    <div className="stat-card" key={pt.label} style={{ height: '110px', minWidth: '200px' }}>
                      <div className="stat-value">{pt.value}</div>
                      <div className="stat-label">{pt.label}</div>
                    </div>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default ClientDashboard; 